import {FormAction, FormState, FormStateWithIssues} from '@raison/form-models';
import {useLocationState} from '@raison/navigation';
import {RsnButton, RsnDialog, RsnInput, RsnTextButton, styles, useBoolean} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {useEffect, useReducer, useState} from 'react';
import {Link, useNavigate} from 'react-router';
import {
    CaptchaErrorDialog,
    CaptchaUsageMention,
    isCaptchaError,
    shouldThrowCaptchaError,
    useCaptcha,
} from '~/entities/captcha';
import {CaptchaError} from '~/entities/captcha/api/useCaptcha';
import {GenericErrorMsg} from '~/entities/dialogs';
import {
    SignupStartRequest,
    SignupVerifyCodeErrorCode,
    useSignupVerifyCodeMutation,
    useVerificationMethodsMutation,
} from '~/entities/user';
import {SignupStartErrorCode, useSignupStartMutation} from '~/entities/user/api/signup';
import {
    FallbackVerificationDialog,
    InvalidCodeDialog,
    SmsErrorDialog,
    SmsErrorStatus,
    TwoFaCodeForm,
    useResendTimer,
    useSmsStatusPolling,
    VerificationMethodDialog,
    VerificationMethodType,
} from '~/features/two-factor';
import {TooManyWrongCodesDialog} from '~/processes/onboarding/ui/shared/TooManyWrongCodesDialog';
import {TooManyFailedAttemptsDialog} from '~/processes/onboarding/ui/signup/TooManyFailedAttemptsDialog';
import {appLocalStorage, appSessionStorage} from '~/shared/persistence';
import {useAppDispatch} from '~/shared/store';
import {useUTM} from '../../lib/useUTM';
import {Username} from '../../model/Username';
import {CodeAlreadySent} from '../shared/CodeAlreadySent';
import {CouldNotSendDialog} from '../shared/CouldNotSendDialog';
import {getHelperText, PhoneNumberInput} from '../shared/PhoneNumberInput';

export type PhoneVerificationScreenProps = {
    onVerificationSuccess: (values: {username: string; uuid: string}) => void;
    isOTP: boolean;
    goToOTP: () => void;
};

export function PhoneVerificationScreen({
    onVerificationSuccess,
    isOTP,
    goToOTP: goToOTPExternal,
}: PhoneVerificationScreenProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const savedUsername = appLocalStorage.username.get();

    const [dialog, openDialog] = useLocationState('signup-phone-verification-screen-dialog', Dialog);
    const closeDialog = () => {
        navigate(-1);
    };

    const [form, dispatchForm] = useReducer(
        FormState.reduce<SignupStartValues>,
        {
            username: savedUsername ? Username.fromString(savedUsername) : Username.empty(),
            inviteCode: '',
            code: '',
        },
        FormState.from
    );

    const formWithIssues = FormStateWithIssues.fromRules(form, {
        username: [validateUsername],
        inviteCode: [validateInviteCode],
    });

    const [captchaError, setCaptchaError] = useState<CaptchaError>();
    const [smsErrorType, setSmsErrorType] = useState<SmsErrorStatus>();
    const [showInviteCodeInput, setShowInviteCodeInput] = useBoolean(false);
    const [retryAfterTs, setRetryAfterTs] = useState(Date.now());
    const [isSubmitting, setIsSubmitting] = useBoolean(false);
    const [isResending, setIsResending] = useBoolean(false);

    const {
        mutateAsync: fetchVerificationMethods,
        data: {value: verificationMethods = []} = {},
        isPending: isVerificationMethodsPending,
    } = useVerificationMethodsMutation();
    const {mutateAsync: signupStart} = useSignupStartMutation();
    const {mutateAsync: verifyCode, isPending: isVerifyCodePending} = useSignupVerifyCodeMutation();

    const utm = useUTM();
    const captcha = useCaptcha();
    const [timer$, timerActions] = useResendTimer();
    const {startPolling} = useSmsStatusPolling();

    function resetCode() {
        dispatchForm(
            FormAction.Update(form => {
                form.fields.code.value = '';
                form.fields.code.wasModified = Date.now();
            })
        );
    }

    const goToOTP = () => {
        resetCode();
        goToOTPExternal();
    };

    useEffect(() => {
        // The default timer state is `pending`, which renders nothing.
        // Switch it to success state with 0 seconds timer to immediately show the "Resend code" button.
        timerActions.success(0);
    }, []);

    const requestCode = async (options: {
        username: string;
        inviteCode: string;
        resending?: boolean;
        method?: VerificationMethodType;
    }) => {
        const resending = options.resending ?? false;
        const verificationMethod = options?.method;
        const username = options?.username;
        const {appVersion, appDeviceId} = window;
        const inviteCode = options.inviteCode;

        appLocalStorage.username.set(username);
        appSessionStorage.inviteCodeSession.set(inviteCode);

        try {
            const captchaToken = await captcha.execute();
            const data: SignupStartRequest = {
                ...utm,
                username,
                invite_code: inviteCode,
                tenant_uuid: '',
                captchaToken,
                verificationMethod,
                app_version: appVersion,
                device_id: appDeviceId,
            };

            if (resending) {
                data.repeated_event = 'yes';
            }

            const {value, problem} = await signupStart(data);

            if (problem) {
                switch (problem.error_code) {
                    case SignupStartErrorCode.alreadyRegistered: {
                        openDialog(Dialogs.couldNotSend);
                        return;
                    }
                    case SignupStartErrorCode.cooldownPeriod: {
                        timerActions.success(problem.retry_in);
                        openDialog(Dialogs.codeAlreadySent);
                        return;
                    }
                    case SignupStartErrorCode.requestLimit: {
                        setRetryAfterTs(Date.now() + problem.retry_in * 1000);
                        openDialog(Dialogs.tooManyAttempts);
                        return;
                    }
                    case SignupStartErrorCode.somethingWrong: {
                        dispatch(GenericErrorMsg.open());
                        return;
                    }
                    default: {
                        dispatch(GenericErrorMsg.open());
                        return;
                    }
                }
            }

            const tenantId = value.tenant_uuid;
            timerActions.success(value.retry_in);
            startPolling({
                onError(errorType) {
                    timerActions.success(0);
                    setSmsErrorType(errorType);
                    openDialog(Dialogs.smsError);
                },
                onSuccess() {
                    if (resending) {
                        openDialog(Dialogs.fallbackVerification);
                    }
                },
                tenantId,
            });
            if (!isOTP) {
                goToOTP();
            }
        } catch (e) {
            if (isCaptchaError(e)) {
                setCaptchaError(e);
                openDialog(Dialogs.captchaError);
                if (shouldThrowCaptchaError(e)) throw e;
            } else {
                dispatch(GenericErrorMsg.open());
                throw e;
            }
        }
    };

    const {fields, onSubmit: handleSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        submit: async () => {
            const username = form.fields.username.value.toString();
            const code = form.fields.code.value;
            if (!isOTP) {
                const inviteCode =
                    form.fields.inviteCode.value || appSessionStorage.inviteCodeSession.get() || utm.invite_code || '';

                const {problem, value} = await fetchVerificationMethods(username).catch(handleError);

                if (problem) {
                    return problem;
                }

                if (value?.length <= 1) {
                    setIsSubmitting.on();
                    requestCode({
                        username,
                        inviteCode,
                        method: verificationMethods?.[0],
                    }).finally(setIsSubmitting.off);
                    return;
                }

                openDialog(Dialogs.verificationMethod);
            } else {
                const {value: uuid, problem} = await verifyCode({
                    phone: username,
                    code,
                }).catch(handleError);

                if (problem) {
                    switch (problem.error_code) {
                        case SignupVerifyCodeErrorCode.codeExpired: {
                            return {
                                code: 'This code has expired',
                            };
                        }
                        case SignupVerifyCodeErrorCode.wrongCode: {
                            openDialog(Dialogs.invalidCode);
                            return;
                        }
                        case SignupVerifyCodeErrorCode.limitReached: {
                            openDialog(Dialogs.tooManyWrongCodes);
                            return;
                        }
                        case SignupVerifyCodeErrorCode.somethingWrong: {
                            dispatch(GenericErrorMsg.open());
                            return;
                        }
                        default: {
                            dispatch(GenericErrorMsg.open());
                            return;
                        }
                    }
                }

                onVerificationSuccess({
                    username,
                    uuid,
                });
            }
            return true;
        },
    });

    const handleVerificationMethodChange = (method: VerificationMethodType) => {
        setIsSubmitting.on();
        requestCode({
            username: form.fields.username.value.toString(),
            inviteCode: form.fields.inviteCode.value,
            method,
        }).finally(setIsSubmitting.off);
    };

    function handleRetryIntent() {
        resetCode();
        setIsResending.on();
        requestCode({
            username: form.fields.username.value.toString(),
            inviteCode: form.fields.inviteCode.value,
            resending: true,
        }).finally(setIsResending.off);
    }

    const handleError = (e: unknown) => {
        dispatch(GenericErrorMsg.open());
        throw e;
    };

    function renderScreen() {
        if (isOTP) {
            return (
                <div className={'p-4'}>
                    <TwoFaCodeForm
                        timer$={timer$}
                        onRetryIntent={handleRetryIntent}
                        otp={fields.code.value}
                        onOtpChange={fields.code.onChange}
                        onInputComplete={() => handleSubmit()}
                        isPending={isResending || isVerifyCodePending}
                        issue={fields.code.issue}
                        heading={
                            <hgroup className={cls.header}>
                                <h2 className={cls.title}>Phone number verification</h2>
                                <small className={cls.subtitle}>
                                    Enter the one-time code we just sent <br />
                                    to <span className={'fv-text-l-bold'}>{form.fields.username.value.formatted}</span>
                                </small>
                            </hgroup>
                        }
                        deliveryInfo={null}
                    />
                </div>
            );
        }

        return (
            <form className={'flex flex-1 flex-col'} onSubmit={handleSubmit}>
                <div className={'flex flex-1 flex-col gap-6 p-4'}>
                    <hgroup className={cls.header}>
                        <h2 className={cls.title}>New account</h2>
                        <small className={cls.subtitle}>
                            Please enter your phone number or email to register <br />
                            or{' '}
                            <Link className={cls.link} to={'/login'}>
                                log in
                            </Link>
                            .
                        </small>
                    </hgroup>

                    <PhoneNumberInput
                        label={<RsnInput.Label>Phone number</RsnInput.Label>}
                        {...fields.username}
                        onChange={value => {
                            fields.username.onChange(value);
                            appLocalStorage.username.set(value.toString());
                        }}
                    />
                    {showInviteCodeInput ? (
                        <RsnInput label={<RsnInput.Label>Invite code</RsnInput.Label>} {...fields.inviteCode} />
                    ) : (
                        <RsnTextButton.Inline underlined size={'m'} onClick={setShowInviteCodeInput.on}>
                            Already have an invitation code?
                        </RsnTextButton.Inline>
                    )}
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <div className={'flex flex-col gap-2'}>
                        <RsnButton.Primary type={'submit'} disabled={isSubmitting || isVerificationMethodsPending}>
                            Get a one-time code
                        </RsnButton.Primary>
                    </div>
                </div>
            </form>
        );
    }

    return (
        <>
            {renderScreen()}
            <CaptchaUsageMention />
            <RsnDialog.Controller
                onClose={closeDialog}
                onExitEnd={() => {
                    if (isOTP) {
                        resetCode();
                        focusOTPInput();
                    }
                }}
            >
                {(() => {
                    switch (true) {
                        case dialog === Dialogs.verificationMethod: {
                            return (
                                <VerificationMethodDialog
                                    methods={verificationMethods || []}
                                    onChange={handleVerificationMethodChange}
                                />
                            );
                        }
                        case dialog === Dialogs.smsError && !!smsErrorType: {
                            return <SmsErrorDialog type={smsErrorType} />;
                        }
                        case dialog === Dialogs.fallbackVerification: {
                            return <FallbackVerificationDialog />;
                        }
                        case dialog === Dialogs.couldNotSend: {
                            return <CouldNotSendDialog />;
                        }
                        case dialog === Dialogs.invalidCode: {
                            return <InvalidCodeDialog />;
                        }
                        case dialog === Dialogs.tooManyAttempts: {
                            return <TooManyFailedAttemptsDialog retryAfterTs={retryAfterTs} />;
                        }
                        case dialog === Dialogs.tooManyWrongCodes: {
                            return <TooManyWrongCodesDialog />;
                        }
                        case dialog === Dialogs.captchaError && !!captchaError: {
                            return <CaptchaErrorDialog error={captchaError} />;
                        }
                    }
                })()}
            </RsnDialog.Controller>
            <RsnDialog.Controller onClose={closeDialog} onExitEnd={goToOTP}>
                {dialog === Dialogs.codeAlreadySent && <CodeAlreadySent />}
            </RsnDialog.Controller>
        </>
    );
}

const cls = styles({
    link: 'fv-text-l-bold text-primary-30',
    header: 'flex flex-col gap-2 pt-2',
    title: 'fv-heading-l text-neutral-100',
    subtitle: 'fv-text-l-regular text-neutral-90',
});

export type SignupStartValues = {
    username: Username;
    inviteCode: string;
    code: string;
};

function focusOTPInput() {
    TwoFaCodeForm.focusOTPInput();
}

const validateInviteCode = (value: string | null) => {
    if (!value) {
        return undefined;
    }
    if (!/^[-~\w\d]{0,20}$/.test(value)) {
        return 'Invalid code';
    }
};

function validateUsername(value: Username) {
    return getHelperText(value.validationError);
}

const Dialogs = {
    smsError: 'sms-error',
    verificationMethod: 'verification-method',
    fallbackVerification: 'fallback-verification',
    codeAlreadySent: 'code-already-sent',
    couldNotSend: 'could-not-send',
    invalidCode: 'invalid-code',
    tooManyAttempts: 'too-many-attempts',
    tooManyWrongCodes: 'too-many-wrong codes',
    captchaError: 'captcha-error',
} as const;
const Dialog = Schema.Literal(...Object.values(Dialogs));
